import angular from 'angular';
import sanitize from 'angular-sanitize';
import angularBootstrap from 'angular-ui-bootstrap';

import i18n from '../i18n';
import indeterminateProgress from '../indeterminate-progress';

import dropdownDirective from './dropdown-directive';
import dropdownTemplateService from './dropdown-template-service';
import appendToBodyService from './append-to-body-service';
import highlight from '../highlight';

/**
 * @ngdoc module
 * @name akamai.components.dropdown
 *
 * @guideline https://design.akamai.com/components/dropdown/
 *
 * @description
 * Dropdown is a form control that allows users to select a single value from a list of possible
 * options. Dropdown supports the use of strings, objects and promises. Also, the
 * `<akam-dropdown-selected>` and `<akam-dropdown-option>` tags can be used to declare custom
 * markup for the selected item and menu items respectively.
 *
 * @example index.html
 * <akam-dropdown ng-model="vm.selectedItem"
 *                items="vm.optionItems"
 *                text-property="name"
 *                key-property="itemId"
 *                placeholder="an.i18n.key"
 *                is-disabled="vm.isDisabled"
 *                is-readonly="vm.isReadonly"
 *                no-items-message="custom.i18n.no.items.message"
 *                item-failure-message="custom.i18n.item.failure.message"
 *                appended-to-body
 *                clearable>
 *
 *     <akam-dropdown-selected>
 *         custom: {% raw %}{{dropdown.selectedItem.name}}{% endraw %}
 *     </akam-dropdown-selected>
 *
 *     <akam-dropdown-option>
 *       <span>custom: {% raw %}{{item.name}}{% endraw %}</span>
 *     </akam-dropdown-option>
 *
 * </akam-dropdown>
 *
 * @example index.js
 * // will be injected "as vm"
 * function MyController() {
 *   this.keys = [
 *     {state: {key: 'key1', name: 'Colorado'}},
 *     {state: {key: 'key2', name: 'Connecticut'}},
 *     {state: {key: 'key3', name: 'Maryland'}}
 *   ];
 * }
 *
 */

export default angular.module('akamai.components.dropdown', [
  angularBootstrap,
  sanitize,
  highlight.name,
  i18n.name,
  indeterminateProgress.name
])

  .service('dropdownTemplateService', dropdownTemplateService)

  .service('appendToBodyService', appendToBodyService)

/**
 * @ngdoc directive
 * @name akamDropdown
 * @description Creates a dropdown control
 * @restrict E
 *
 * @param {(Object|string)} ng-model The selected item.
 *
 * @param {(Object[]|string[]|Promise)} items A list of selectable menu items.
 *
 * @param {string} [text-property] This attribute is only applicable and required when menu items
 * are objects. It defines the property used for rendering the selected item and menu items. The
 * menu item values must be unique. `text-property` can be nested and will be parsed accordingly
 * (e.g. `text-property="foo.bar.label"`).
 *
 * @param {string} [key-property] This attribute is only applicable when menu items are objects.
 * If `key-property` is defined, the corresponding property for the selected object is
 * bound to `ng-model`. If `key-property` is not defined, the selected object itself is bound to
 * `ng-model`.
 *
 * @param {string} [disabled-property] This attribute is only applicable when menu items are
 * objects. If `disabled-property` is defined, any menu items that have the corresponding property
 * set to the boolean true or a function which returns the boolean true will have a css class
 * defined as 'disabled-dropdown-item' on its wrapping anchor tag. Additionally clicking on any
 * of these disabled items will not change the selected item.
 *
 * @param {string|TranslateKey} [placeholder=Select one] The placeholder text for the selected item
 * field. When using custom markup, include `{{dropdown.placeholder}}` to display the default
 * placeholder text.
 *
 * ```
 * <akam-dropdown>
 *   <akam-dropdown-selected>
 *     {% raw %}{{dropdown.placeholder}}{% endraw %}
 *    </akam-dropdown-selected>
 * </akam-dropdown>
 * ```
 *
 * @param {boolean} [is-disabled=false] If disabled, no user interaction will be possible.
 *
 * @param {boolean} [is-readonly=false] If readonly, no user interaction will be possible but the
 * text will be easier to read.
 **
 * @param {*} [appended-to-body] If present, the dropdown menu will be appended to the body. Use
 * this when using dropdown in a container that is set to `overflow: hidden`.
 *
 * @param {*} [clearable] If present, the selected item will be clearable.
 *
 * @param {string|TranslateKey} [item-failure-message=components.dropdown.itemFailureMessage]
 * The text to show the user when the items property fails to load (promise is rejected). If not
 * set, the directive uses the default akamai-core item failure message.
 *
 * @param {string|TranslateKey} [no-items-message=components.dropdown.noItemsMessage]
 * The text to show the user when there are zero items to choose from. If not set, the directive
 * uses the default akamai-core no items message.
 *
 */
  .directive('akamDropdown', dropdownDirective);
